// Readiness policy — what "go-live ready" means for a program: an attendance
// threshold plus optional critical-session, practice and verification
// requirements. Controlled: emits the full ReadinessPolicy shape on every
// change (enabled=false keeps the settings so re-enabling restores them).
// Evaluation is the server's (GET /programs/{id}/readiness).
import { useT } from '../lib/i18n';
import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Switch } from '@/features/shared/components/ui/switch';
import type { ReadinessPolicy } from '@/services/learningService';

export const defaultReadinessPolicy = (): ReadinessPolicy => ({
  enabled: true, attendance_pct: 80, require_critical_sessions: true, require_practices: false, require_verification: false,
});

interface Props {
  value: ReadinessPolicy | null | undefined;
  onChange: (policy: ReadinessPolicy) => void;
  /** When given, a Save button is rendered and the editor calls it with the current value. */
  onSave?: (policy: ReadinessPolicy) => void;
  busy?: boolean;
  disabled?: boolean;
  idPrefix?: string;
  className?: string;
}

export const ReadinessPolicyEditor: React.FC<Props> = ({ value, onChange, onSave, busy, disabled, idPrefix = 'rp', className }) => {
  const t = useT();
  const v: ReadinessPolicy = value ? { ...defaultReadinessPolicy(), ...value } : { ...defaultReadinessPolicy(), enabled: false };
  const patch = (p: Partial<ReadinessPolicy>) => onChange({ ...v, ...p });
  const pct = Number.isFinite(v.attendance_pct) ? v.attendance_pct : 0;
  const pctOk = pct >= 0 && pct <= 100;
  const flags: Array<[keyof ReadinessPolicy, string, string]> = [
    ['require_critical_sessions', t('training:rp_critical', 'Critical sessions must be attended'), t('training:rp_critical_help', 'Sessions flagged "Critical for readiness" must be attended regardless of the threshold.')],
    ['require_practices', t('training:rp_practices', 'Practical exercises must be validated'), t('training:rp_practices_help', 'Every bound practical exercise must be validated.')],
    ['require_verification', t('training:rp_verification', 'Completions must be verified'), t('training:rp_verification_help', 'Sign-offs and verifications still pending keep a person "at risk".')],
  ];
  return (
    <div className={`space-y-3 ${className || ''}`} data-testid="readiness-policy-editor">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label htmlFor={`${idPrefix}-enabled`} className="text-sm font-medium">{t('training:rp_enabled', 'Track go-live readiness')}</Label>
          <p className="text-[11px] text-muted-foreground">{t('training:rp_enabled_help', 'The platform rates every participant ready · at risk · not ready against these rules.')}</p>
        </div>
        <Switch id={`${idPrefix}-enabled`} checked={v.enabled} disabled={disabled || busy} onCheckedChange={(on) => patch({ enabled: on })} aria-label={t('training:rp_enabled', 'Track go-live readiness')} />
      </div>
      {v.enabled && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-pct`} className="text-xs">{t('training:rp_attendance_pct', 'Required attendance (%)')}</Label>
            <Input id={`${idPrefix}-pct`} className="h-9" type="number" min={0} max={100} value={v.attendance_pct} disabled={disabled || busy}
              onChange={(e) => patch({ attendance_pct: e.target.value === '' ? 0 : Number(e.target.value) })} />
            {!pctOk && <p role="alert" className="text-[11px] text-red-600 dark:text-red-400">{t('training:rp_pct_range', 'Attendance must be between 0 and 100.')}</p>}
          </div>
          <div className="space-y-2 sm:col-span-2">
            {flags.map(([k, label, help]) => (
              <label key={k} className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="accent-orange-500 mt-0.5" checked={!!v[k]} disabled={disabled || busy} onChange={(e) => patch({ [k]: e.target.checked } as Partial<ReadinessPolicy>)} />
                <span><span className="text-foreground">{label}</span><span className="block text-[11px] text-muted-foreground">{help}</span></span>
              </label>
            ))}
          </div>
        </div>
      )}
      {onSave && (
        <div className="flex justify-end">
          <Button size="sm" disabled={busy || disabled || (v.enabled && !pctOk)} onClick={() => onSave(v)} data-testid={`${idPrefix}-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:save', 'Save')}</Button>
        </div>
      )}
    </div>
  );
};

export default ReadinessPolicyEditor;
